import { useTranslation } from "@sagot/i18n";
import { Dialog, DialogContent, Icon, type IconName } from "@sagot/ui";
import type { ActionIntegration } from "../shared/types";
import type { TemplateDefaults } from "./schema-model";

interface TemplateTile {
  integration: ActionIntegration;
  icon: IconName;
  defaults: Omit<TemplateDefaults, "name" | "integration">;
}

/** HTTP-based templates; the booking tile above them creates a built-in booking action. */
export const ACTION_TEMPLATES: readonly TemplateTile[] = [
  { integration: "custom_api", icon: "code", defaults: { method: "GET", url: "https://" } },
  { integration: "webhook", icon: "webhook", defaults: { method: "POST", url: "https://" } },
  { integration: "zapier", icon: "workflow", defaults: { method: "POST", url: "https://hooks.zapier.com/hooks/catch/" } },
];

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (template: TemplateDefaults) => void;
  /** Built-in booking action (tour, demo, appointment): created at once, then opened. */
  onPickBooking: () => void;
  bookingPending: boolean;
}

const TILE_CLASS = "rounded-lg border border-line p-4 text-left hover:border-primary hover:bg-primary-soft disabled:opacity-60";

/** `new-action-modal`: template gallery → editor prefilled. */
export function TemplateDialog({ open, onOpenChange, onPick, onPickBooking, bookingPending }: TemplateDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t("build.actions.templates.title")} description={t("build.actions.templates.subtitle")} closeLabel={t("common.close")} className="max-w-2xl">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          <button type="button" className={TILE_CLASS} disabled={bookingPending} aria-busy={bookingPending || undefined} onClick={onPickBooking} data-testid="template-booking">
            <Icon name="calendar" className="size-5 text-primary-ink" />
            <span className="mt-2 block text-sm font-bold">{t("build.actions.templates.booking")}</span>
            <span className="block text-xs text-fg-subtle">{t("build.actions.templates.bookingBody")}</span>
          </button>
          {ACTION_TEMPLATES.map((tile) => (
            <button
              key={tile.integration}
              type="button"
              className={TILE_CLASS}
              onClick={() => { onPick({ ...tile.defaults, integration: tile.integration, name: t(`build.actions.templates.${tile.integration}`) }); }}
            >
              <Icon name={tile.icon} className="size-5 text-primary-ink" />
              <span className="mt-2 block text-sm font-bold">{t(`build.actions.templates.${tile.integration}`)}</span>
              <span className="block text-xs text-fg-subtle">{t(`build.actions.templates.${tile.integration}Body`)}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
